import React, { useState, useContext, useCallback } from "react";
import axios from "axios";
import { ClientGroupsContext, ClientsContext } from "@/App";
import { PRESET_COLORS } from "@/components/CreateClientModal";
import { divisionColor, divisionLabel } from "@/lib/divisions";
import { Palette, Trash2, Plus, Check, X, ChevronRight, ChevronDown, Pencil, ImagePlus } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientGroupsPanel() {
  const { clientGroups, fetchClientGroups } = useContext(ClientGroupsContext);
  const { clients } = useContext(ClientsContext);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", type: "group", parent_id: null, color: "#3b82f6" });

  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", color: "" });
  const [colorPickerFor, setColorPickerFor] = useState(null);
  const [addingClientsTo, setAddingClientsTo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogoId, setUploadingLogoId] = useState(null);

  const topLevelGroups = clientGroups.filter(g => !g.parent_id || g.parent_id === null);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleCreate = useCallback(async () => {
    if (!createForm.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/client-groups`, {
        name: createForm.name.trim(),
        type: createForm.type,
        parent_id: createForm.type === "subgroup" ? createForm.parent_id : null,
        color: createForm.color,
      });
      await fetchClientGroups();
      setShowCreate(false);
      setCreateForm({ name: "", type: "group", parent_id: null, color: "#3b82f6" });
      toast.success("Group created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create group");
    } finally { setSaving(false); }
  }, [createForm, fetchClientGroups]);

  const handleSaveName = useCallback(async (group) => {
    if (!editForm.name.trim()) return;
    try {
      await axios.put(`${API}/api/client-groups/${group.id}`, { name: editForm.name.trim() });
      await fetchClientGroups();
      setEditingId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename group");
    }
  }, [editForm.name, fetchClientGroups]);

  const handleSaveColor = useCallback(async (group, color) => {
    try {
      await axios.put(`${API}/api/client-groups/${group.id}`, { color });
      await fetchClientGroups();
      setColorPickerFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update color");
    }
  }, [fetchClientGroups]);

  const handleDelete = useCallback(async (group) => {
    if (!window.confirm(`Delete group "${group.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/client-groups/${group.id}`);
      await fetchClientGroups();
      toast.success("Group deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete group");
    }
  }, [fetchClientGroups]);

  const handleToggleClient = useCallback(async (group, clientId) => {
    const current = group.client_ids || [];
    const newIds = current.includes(clientId)
      ? current.filter(id => id !== clientId)
      : [...current, clientId];
    try {
      await axios.put(`${API}/api/client-groups/${group.id}`, { client_ids: newIds });
      await fetchClientGroups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update clients");
    }
  }, [fetchClientGroups]);

  const handleUploadGroupLogo = useCallback(async (group) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".svg";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.name.endsWith(".svg")) { toast.error("Only .svg files are allowed"); return; }
      if (file.size > 50000) { toast.error("SVG file too large (max 50KB)"); return; }
      setUploadingLogoId(group.id);
      try {
        const text = await file.text();
        await axios.put(`${API}/api/client-groups/${group.id}`, { logo_svg: text });
        await fetchClientGroups();
        toast.success("Group logo updated");
      } catch (err) {
        toast.error("Failed to upload logo");
      } finally {
        setUploadingLogoId(null);
      }
    };
    input.click();
  }, [fetchClientGroups]);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const assignedClientIds = new Set(clientGroups.flatMap(g => g.client_ids || []));
  const ungroupedCount = clients.filter(c => !assignedClientIds.has(c.id)).length;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Section header */}
      <div className="mt-6 mb-2 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Client Groups</p>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{clientGroups.length} groups</span>
          <button
            onClick={() => { setShowCreate(v => !v); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
            style={{ background: "rgba(99,102,241,0.08)", color: "#f97316", border: "1px solid rgba(99,102,241,0.2)" }}
            data-testid="add-client-group-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New group
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="mb-3 p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Group name"
              value={createForm.name}
              onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
              onKeyDown={e => { if (e.key === "Enter") handleCreate(); if (e.key === "Escape") setShowCreate(false); }}
              className="flex-1 h-7 px-2 text-sm rounded bg-[hsl(var(--muted))] border border-[hsl(var(--border))] focus:outline-none focus:ring-1 focus:ring-orange-500"
              autoFocus
              data-testid="create-group-name-input"
            />
            <select
              value={createForm.type}
              onChange={e => setCreateForm(f => ({ ...f, type: e.target.value, parent_id: null }))}
              className="h-7 px-2 text-xs rounded bg-[hsl(var(--muted))] border border-[hsl(var(--border))] focus:outline-none"
            >
              <option value="group">Group</option>
              <option value="subgroup">Subgroup</option>
            </select>
          </div>

          {createForm.type === "subgroup" && (
            <select
              value={createForm.parent_id || ""}
              onChange={e => setCreateForm(f => ({ ...f, parent_id: e.target.value || null }))}
              className="w-full h-7 px-2 text-xs rounded bg-[hsl(var(--muted))] border border-[hsl(var(--border))] focus:outline-none"
            >
              <option value="">Select parent group…</option>
              {topLevelGroups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1.5">Color</p>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setCreateForm(f => ({ ...f, color: c }))}
                  className="w-6 h-6 rounded-full border-2 transition-all hover:scale-110"
                  style={{
                    backgroundColor: c,
                    borderColor: createForm.color === c ? "#fff" : "transparent",
                    boxShadow: createForm.color === c ? `0 0 0 2px ${c}` : "none",
                  }}
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-[hsl(var(--border))]/50">
              <input
                type="text"
                placeholder="#hex"
                defaultValue={createForm.color}
                maxLength={7}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const val = e.target.value.trim();
                    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
                      setCreateForm(f => ({ ...f, color: val }));
                    }
                  }
                }}
                className="w-[72px] h-7 px-2 text-xs font-mono-data rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
              <label className="relative cursor-pointer">
                <input
                  type="color"
                  value={createForm.color}
                  onChange={(e) => setCreateForm(f => ({ ...f, color: e.target.value }))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div
                  className="w-7 h-7 rounded-lg border-2 border-[hsl(var(--border))] hover:scale-110 transition-transform flex items-center justify-center"
                  style={{ backgroundColor: createForm.color }}
                  title="Pick from screen"
                >
                  <Palette className="w-3 h-3 text-white drop-shadow" style={{ opacity: 0.7 }} />
                </div>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleCreate}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors"
              data-testid="create-group-save-btn"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Group list */}
      <div className="space-y-2 mb-3">
        {clientGroups.map(group => {
          const isExpanded = expandedId === group.id;
          const isEditing = editingId === group.id;
          const groupClientObjs = (group.client_ids || [])
            .map(cid => clients.find(c => c.id === cid))
            .filter(Boolean);

          return (
            <div key={group.id} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
              {/* Row */}
              <div className="flex items-center gap-2 px-3 py-2.5">
                {/* Color dot + color picker */}
                <div className="relative shrink-0">
                  <button
                    onClick={() => setColorPickerFor(prev => prev === group.id ? null : group.id)}
                    className="w-8 h-8 rounded-full border-2 border-white shadow-sm transition-transform hover:scale-110 flex items-center justify-center"
                    style={{ backgroundColor: group.color }}
                    title="Edit color"
                    data-testid={`group-color-btn-${group.id}`}
                  >
                    <Palette className="w-3.5 h-3.5 text-white drop-shadow" style={{ opacity: 0.8 }} />
                  </button>
                  {colorPickerFor === group.id && (
                    <div
                      className="absolute left-0 top-10 z-50 p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl"
                      style={{ minWidth: "200px" }}
                    >
                      <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">Pick a color</p>
                      <div className="flex flex-wrap gap-1.5">
                        {PRESET_COLORS.map(c => (
                          <button
                            key={c}
                            onClick={() => handleSaveColor(group, c)}
                            className="w-6 h-6 rounded-full border-2 transition-all hover:scale-110"
                            style={{
                              backgroundColor: c,
                              borderColor: group.color === c ? "#fff" : "transparent",
                              boxShadow: group.color === c ? `0 0 0 2px ${c}` : "none",
                            }}
                          />
                        ))}
                      </div>
                      {/* Hex input + native color picker */}
                      <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-[hsl(var(--border))]/50">
                        <input
                          type="text"
                          placeholder="#hex"
                          defaultValue={group.color}
                          maxLength={7}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              const val = e.target.value.trim();
                              if (/^#[0-9a-fA-F]{6}$/.test(val)) {
                                handleSaveColor(group, val);
                              }
                            }
                          }}
                          className="w-[72px] h-7 px-2 text-xs font-mono-data rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 focus:outline-none focus:ring-1 focus:ring-orange-500"
                        />
                        <label className="relative cursor-pointer">
                          <input
                            type="color"
                            value={group.color}
                            onChange={(e) => handleSaveColor(group, e.target.value)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <div
                            className="w-7 h-7 rounded-lg border-2 border-[hsl(var(--border))] hover:scale-110 transition-transform flex items-center justify-center"
                            style={{ backgroundColor: group.color }}
                            title="Pick from screen"
                          >
                            <Palette className="w-3 h-3 text-white drop-shadow" style={{ opacity: 0.7 }} />
                          </div>
                        </label>
                      </div>
                      <button
                        onClick={() => setColorPickerFor(null)}
                        className="mt-2 text-[10px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                        onKeyDown={e => { if (e.key === "Enter") handleSaveName(group); if (e.key === "Escape") setEditingId(null); }}
                        className="flex-1 h-7 px-2 text-sm rounded bg-[hsl(var(--muted))] border border-[hsl(var(--border))] focus:outline-none focus:ring-1 focus:ring-orange-500"
                        autoFocus
                        data-testid={`group-name-edit-${group.id}`}
                      />
                      <button onClick={() => handleSaveName(group)} className="p-1 rounded text-green-500 hover:bg-green-500/10">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-1 rounded text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-sm font-semibold px-2 py-0.5 rounded"
                        style={{ color: group.color, backgroundColor: group.color + "18" }}
                      >
                        {group.name}
                      </span>
                      {/* Naming locked 13-Jul: hierarchy reads Division → client
                          group → client. Top-level groups WITH a division attr
                          get the DIVISION badge; second-level groups read GROUP. */}
                      {group.type === "subgroup" ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] font-medium uppercase tracking-wider">
                          group
                        </span>
                      ) : group.division ? (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider"
                          style={{ color: divisionColor(group.division), background: divisionColor(group.division) + "1c" }}
                          title={`This group is the ${divisionLabel(group.division)} division`}
                        >
                          division
                        </span>
                      ) : null}
                      <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
                        {groupClientObjs.length} client{groupClientObjs.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleUploadGroupLogo(group)}
                    disabled={uploadingLogoId === group.id}
                    className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-orange-500 transition-colors disabled:opacity-40"
                    title={group.logo_svg ? "Replace group logo" : "Upload SVG logo"}
                    data-testid={`group-logo-btn-${group.id}`}
                  >
                    {group.logo_svg ? (
                      <span
                        className="flex items-center justify-center w-3.5 h-3.5"
                        dangerouslySetInnerHTML={{ __html: group.logo_svg }}
                      />
                    ) : (
                      <ImagePlus className="w-3.5 h-3.5" style={{ opacity: 0.5 }} />
                    )}
                  </button>
                  <button
                    onClick={() => { setEditingId(group.id); setEditForm({ name: group.name, color: group.color }); setExpandedId(null); }}
                    className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-orange-500 transition-colors"
                    title="Rename"
                    data-testid={`group-edit-btn-${group.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(group)}
                    className="p-1.5 rounded hover:bg-red-500/10 text-[hsl(var(--muted-foreground))] hover:text-red-500 transition-colors"
                    title="Delete group"
                    data-testid={`group-delete-btn-${group.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => { setExpandedId(isExpanded ? null : group.id); setAddingClientsTo(null); }}
                    className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] transition-colors"
                    data-testid={`group-expand-btn-${group.id}`}
                  >
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {/* Expanded area */}
              {isExpanded && (
                <div className="border-t border-[hsl(var(--border))] px-3 py-3 space-y-3">
                  {/* Client pills */}
                  <div className="flex flex-wrap gap-1.5">
                    {groupClientObjs.length === 0 && (
                      <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic">No clients assigned</span>
                    )}
                    {groupClientObjs.map(client => (
                      <span
                        key={client.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                        style={{ backgroundColor: client.color + "20", color: client.color, border: `1px solid ${client.color}40`, cursor: "pointer" }}
                        onMouseDown={(e) => {
                          if (e.button === 1) {
                            e.preventDefault();
                            handleToggleClient(group, client.id);
                          }
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: client.color }}
                        />
                        {client.name}
                        <button
                          onClick={() => handleToggleClient(group, client.id)}
                          className="ml-0.5 hover:opacity-70 transition-opacity"
                          title={`Remove ${client.name} from group`}
                          data-testid={`group-remove-client-${group.id}-${client.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>

                  {/* Assign clients toggle */}
                  <div>
                    <button
                      onClick={() => setAddingClientsTo(prev => prev === group.id ? null : group.id)}
                      className="text-[11px] font-semibold text-orange-500 hover:text-orange-400 transition-colors flex items-center gap-1"
                      data-testid={`group-assign-clients-btn-${group.id}`}
                    >
                      <Plus className="w-3 h-3" />
                      {addingClientsTo === group.id ? "Done assigning" : "Assign client"}
                    </button>

                    {addingClientsTo === group.id && (
                      <div className="mt-2 space-y-1 max-h-48 overflow-y-auto pr-1">
                        {clients.map(client => {
                          const checked = (group.client_ids || []).includes(client.id);
                          return (
                            <label
                              key={client.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[hsl(var(--muted))] cursor-pointer transition-colors"
                              data-testid={`group-client-checkbox-${group.id}-${client.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleToggleClient(group, client.id)}
                                className="w-3.5 h-3.5 rounded accent-orange-500"
                              />
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: client.color }}
                              />
                              <span className="text-xs font-medium">{client.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Ungrouped count */}
      <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-4">
        {ungroupedCount} client{ungroupedCount !== 1 ? "s" : ""} not yet assigned to a group.
      </p>
    </div>
  );
}
